// ============================================================
// attack/armed - arming a swing: everything decided before the die.
//
// The slot (weapon overrides drive ALL attack math), the ammo check and
// spend, the undo snapshot, Dread, and the modified skill with its
// breakdown. No dice are rolled here.
// ============================================================
import { onHitExposure, type OnHitExposure } from '../../engine/exposures';
import { beatKindFacts, type BeatKindFacts } from './beatFacts';
import { hitMode } from '../../engine/attackCalc';
import { getAttackConditionModifiers } from '../../engine/conditionModifiers';
import { ammoCostPerTrigger } from '../../engine/fireModes';
import type { CountdownResult } from '../../engine/countdown';
import type { ModifierDetail } from '../../engine/structuredNarrative';
import { calcModifiedSkill, calcSkillBreakdown, applyWeaponOverrides, describeOverrides } from '../../engine/index';
import type { Slot, Tab } from '../../types';
import { actorReader, aimedBearer, type AttackContext } from './context';
import type { HitInput } from './shot';

/**
 * Official Overheats (v8.198.1) is a prompt the component owns - the attack
 * itself resolves normally. `overheatDmg` stays in the narrative schema as a
 * permanent 0 so old logs render.
 */
export const OVERHEAT_DMG = 0;

/** An armed swing: everything decided before the die is rolled. */
export interface Swing {
  tab: Tab;
  slot: Slot;
  sp: string[];
  isMelee: boolean;
  cdResult: CountdownResult | null;
  /** The mod that was rolled: the caller's plus the condition modifiers. */
  attackMod: number;
  modifiedSkill: number;
  skillBreakdown: ModifierDetail[];
  /** The attack's special notes, appended to all the way down. */
  notes: string[];
  /** The aimed target's on-hit exposures: luck, jam floor, overheat. */
  onHit: OnHitExposure;
  /** The attack's kind and rate for a miss's or a jam's beat, read once (hooks/attack/beatFacts). */
  beatKind: BeatKindFacts;
}

export const targetNameOf = (tab: Tab): string => tab.target.name || tab.label || '';

function reportNoAmmo(ctx: AttackContext, slot: Slot, ammoCost: number): void {
  const left = slot.ammo?.current || 0;
  ctx.addNarrative(ctx.activeTab, {
    type: 'info',
    text: '🔴 ' + slot.weaponName + ': Not enough ammo! (' + left + ' remaining, need ' + ammoCost + '). Reload first.',
    timestamp: Date.now(),
  });
}

/**
 * Spend the rounds. From the RAW slots (`currentSlots`), never the
 * overridden one: overrides must never persist into the synced profiles
 * (gotchas.md, "ammo write-back uses the RAW slot").
 */
function spendAmmo(ctx: AttackContext, slotIndex: number, ammoCost: number): void {
  const updSlots = [...ctx.currentSlots];
  const raw = updSlots[slotIndex];
  updSlots[slotIndex] = { ...raw, ammo: { ...raw.ammo, current: Math.max(0, (raw.ammo?.current || 0) - ammoCost) } };
  ctx.setAtkProfiles((p) => ({ ...p, list: { ...p.list, [ctx.localCurrentProfile]: updSlots } }));
}

/**
 * Condition modifiers on the ATTACK roll (v7.0.0): the attacker's own
 * Stunned/Fatigue/Blinded/Immobilised against the governing characteristic,
 * plus the defender-Prone posture bonus - routed through conditionModifiers
 * so the forecast engines cannot drift.
 */
function attackConditionMods(ctx: AttackContext, tab: Tab, isMelee: boolean) {
  if (!ctx.applyConditionModifiers) return { total: 0, labels: [], entries: [] as { label: string; value: number }[] };
  return getAttackConditionModifiers(
    ctx.actorConditionContext ?? { conditions: [] },
    tab.target,
    isMelee,
    ctx.condModOptions,
    { charge: ctx.isCharge, rangeBand: ctx.rangeBand },
  );
}

/** The modified skill and its breakdown - the breakdown given THE MOD THAT WAS ROLLED. */
function skillFor(ctx: AttackContext, slot: Slot, tab: Tab, isMelee: boolean) {
  const condMods = attackConditionMods(ctx, tab, isMelee);
  const attackMod = ctx.attackMod + condMods.total;
  // A `snap` exposure on the target (v8.356.0) refuses the aim.
  const aimAllowed = !('noAim' in condMods && condMods.noAim);
  const stats = ctx.attackerCharStats;
  const modifiedSkill = calcModifiedSkill(
    slot,
    attackMod,
    tab.target.size,
    ctx.rangeBand,
    isMelee,
    ctx.hatred,
    ctx.frenzied,
    stats?.removeLongRangePenalty ?? false,
    stats?.removeExtremeRangePenalty ?? false,
    (ctx.isAimHalf || ctx.isAimFull) && aimAllowed,
  );
  const skillBreakdown = breakdownFor(ctx, { slot, tab, isMelee, attackMod, aimAllowed, entries: condMods.entries });
  return { attackMod, modifiedSkill, skillBreakdown };
}

interface BreakdownInput {
  slot: Slot;
  tab: Tab;
  isMelee: boolean;
  attackMod: number;
  aimAllowed: boolean;
  entries: { label: string; value: number }[];
}

/** Declarative: the breakdown's inputs, field for field. */
function breakdownFor(ctx: AttackContext, b: BreakdownInput): ModifierDetail[] {
  return calcSkillBreakdown({
    slot: b.slot,
    // THE MOD THAT WAS ROLLED, not the one before conditions: handing it the
    // base mod made it subtract penalties from a total that never had them
    // (a mysterious positive "Attack Modifier", v7.75.0).
    attackMod: b.attackMod,
    // Named, so the narrative reads "Stunned −20 / Target Prone +10" - and
    // since v8.221.0 the user's own labelled modifiers ride along too.
    customModifiers: [...b.entries, ...ctx.attackCustomMods],
    targetSize: b.tab.target.size,
    rangeBand: ctx.rangeBand,
    isMelee: b.isMelee,
    hatred: ctx.hatred,
    frenzied: ctx.frenzied,
    removeLongRangePenalty: ctx.attackerCharStats?.removeLongRangePenalty ?? false,
    removeExtremeRangePenalty: ctx.attackerCharStats?.removeExtremeRangePenalty ?? false,
    isAimHalf: ctx.isAimHalf && b.aimAllowed,
    isAimFull: ctx.isAimFull && b.aimAllowed,
    isAllOutAttack: ctx.isAllOutAttack,
    isGuardedAttack: ctx.isGuardedAttack,
    isCharge: ctx.isCharge,
    isGangUp: ctx.isGangUp,
    hasBerserkCharge: ctx.hasBerserkCharge,
    isCalledShot: ctx.isCalledShot,
    calledShotReduction: ctx.calledShotReduction,
    calledShotExposure: ctx.calledShotExposure,
    calledShotIgnored: ctx.calledShotIgnored,
  });
}

/**
 * The slot to swing - overridden, since overrides drive ALL attack math -
 * and what one pull of the trigger costs. Null for an empty slot, or for
 * too little ammo (said in the log).
 */
function loadSlot(ctx: AttackContext, slotIndex: number) {
  const rawSlot = ctx.currentSlots[slotIndex];
  if (!rawSlot || rawSlot.isEmpty) return null;
  const slot = applyWeaponOverrides(rawSlot, ctx.weaponOverrides);
  const overrideNotes = describeOverrides(rawSlot, ctx.weaponOverrides);
  const isMelee = slot.weaponType === 'melee';
  // Cost maths live in engine/fireModes so the selector's tooltips and this check agree (v8.234.0).
  const ammoCost = isMelee ? 0 : ammoCostPerTrigger(slot, ctx.fireMode);
  if (!isMelee && (slot.ammo?.current || 0) < ammoCost) {
    reportNoAmmo(ctx, slot, ammoCost);
    return null;
  }
  return { slot, overrideNotes, isMelee, ammoCost };
}

/**
 * Arm the swing: the slot and its ammo check, the undo snapshot, Dread
 * (v8.354.0: before the ammo, before the die), the ammo spent, and the
 * modified skill. Null = no attack.
 */
export function armSwing(
  ctx: AttackContext,
  tab: Tab,
  slotIndex: number,
  cdResult: CountdownResult | null,
): Swing | null {
  const loaded = loadSlot(ctx, slotIndex);
  if (!loaded) return null;
  const { slot, isMelee, ammoCost } = loaded;
  ctx.capturePreActionSnapshot();
  ctx.pushHistory();
  if (ctx.onDread && !ctx.onDread({ target: tab.target, targetId: ctx.activeTab, isMelee })) return null;
  if (!isMelee && ammoCost > 0) spendAmmo(ctx, slotIndex, ammoCost);
  const onHit = onHitExposure(aimedBearer(ctx), actorReader(ctx, isMelee));
  const sp = slot.specialRules || [];
  const skill = skillFor(ctx, slot, tab, isMelee);
  const beatKind = beatKindFacts(ctx, isMelee, hitMode(ctx.fireMode, slot, isMelee, false).effectiveMode);
  return { tab, slot, sp, isMelee, cdResult, ...skill, notes: [...loaded.overrideNotes], onHit, beatKind };
}

/** The swing as a hit, for `resumeAttackFromHit`. */
export function hitFrom(ctx: AttackContext, sw: Swing, attackRoll: number): HitInput {
  return {
    attackRoll,
    modifiedSkill: sw.modifiedSkill,
    slot: sw.slot,
    notes: sw.notes,
    overheatDmg: OVERHEAT_DMG,
    cdResult: sw.cdResult,
    tabId: ctx.activeTab,
    tab: sw.tab,
    isBonusAttack: false,
    skillBreakdown: sw.skillBreakdown,
  };
}
